import { RegisterComponent } from '@/common/register-component';
import { NylasSchedulerConfigConnector } from '@/connector/nylas-scheduler-config-connector';
import { debug } from '@/utils/utils';
import { Calendar } from '@nylas/core';
import { AttachInternals, Component, Element, Event, EventEmitter, Host, Listen, Prop, State, Watch, h } from '@stencil/core';
import { NylasSchedulerEditor } from '../nylas-scheduler-editor/nylas-scheduler-editor';
import { User } from '@/common/nylas-api-request';
import i18next from '@/utils/i18n';
import { LANGUAGE_CODE } from '@/common/constants';
import { ThemeConfig } from '@nylas/core';
/**
 * The `nylas-calendar-picker` component is a form input for selecting calendars to check availability.
 *
 * @part ncp - The calendar picker container
 * @part ncp__dropdown - The dropdown container
 * @part ncp__dropdown-button - The dropdown button
 * @part ncp__dropdown-content - The dropdown content
 */
@Component({
  tag: 'nylas-calendar-picker',
  styleUrl: 'nylas-calendar-picker.scss',
  shadow: true,
  formAssociated: true,
})
export class NylasCalendarPicker {
  @Element() host!: HTMLNylasCalendarPickerElement;
  /**
   * @standalone
   * The name of the calendar picker.
   */
  @Prop() name: string = 'calendar';
  /**
   * @standalone
   * The logged in user.
   */
  @Prop() currentUser?: User;
  /**
   * @standalone
   * The calendars to choose from.
   */
  @Prop() calendars?: Calendar[];
  /**
   * @standalone
   * The default selected calendars.
   */
  @Prop() defaultSelectedCalendars: string[] = [];
  /**
   * The selected language.
   */
  @Prop() selectedLanguage?: LANGUAGE_CODE;
  /**
   * @standalone
   * The theme configuration.
   */
  @Prop({ attribute: 'theme-config' }) readonly themeConfig?: ThemeConfig;
  /**
   * The element internals.
   */
  @AttachInternals() internals!: ElementInternals;

  /**
   * The selected calendars.
   */
  @State() selectedCalendars: string[] = [];

  /**
   * The error message to display.
   */
  @State() error: string = '';

  /**
   * This event is fired when the selected calendars change.
   */
  @Event() valueChanged!: EventEmitter<{
    value: string[];
    name: string;
    valueChangedHandler?: (event: CustomEvent<{ value: string[]; name: string }>) => void;
  }>;

  /**
   * When a name prop is passed, stencil does not automatically set the name attribute on the host element.
   * Since this component is form-associated, the name attribute is required for form submission.
   * This is a workaround to ensure that the name attribute is set on the host element.
   */
  @Watch('name')
  elementNameChangedHandler(newValue: string) {
    debug('nylas-calendar-picker', 'elementNameChangedHandler', newValue);
    this.host.setAttribute('name', newValue);
  }

  @Watch('selectedLanguage')
  selectedLanguageChangedHandler(newVal: LANGUAGE_CODE, oldVal: LANGUAGE_CODE) {
    if (newVal === oldVal) return;
    i18next.changeLanguage(newVal);
    if (this.error) {
      this.error = i18next.t('nylasCalendarPicker.selectAtLeastOneCalendar');
    }
  }

  @Watch('themeConfig')
  themeConfigChangedHandler(newVal: ThemeConfig, oldVal: ThemeConfig) {
    if (newVal === oldVal) return;
    this.applyThemeConfig(newVal);
  }

  // Lifecycle methods
  connectedCallback() {
    debug('nylas-calendar-picker', 'connectedCallback');
    // Use queueMicrotask to defer theme application until after React has set props.
    // This fixes the timing issue where connectedCallback fires before React sets themeConfig.
    queueMicrotask(() => {
      if (this.themeConfig) {
        this.applyThemeConfig(this.themeConfig);
        this.host.setAttribute('data-themed', '');
      }
    });
  }

  disconnectedCallback() {
    debug('nylas-calendar-picker', 'disconnectedCallback');
  }

  componentWillLoad() {
    debug('nylas-calendar-picker', 'componentWillLoad');
    // See comment in the @Watch('name') decorator for more information.
    this.host.setAttribute('name', this.name);
  }

  componentDidLoad() {
    debug('nylas-calendar-picker', 'componentDidLoad');
    // TODO: Remove this when the internals in tests are fixed.
    if (typeof this.internals.setFormValue === 'function') {
      this.internals.setFormValue(this.defaultSelectedCalendars.toString(), this.name);
    }
  }

  componentWillUpdate() {
    debug('nylas-calendar-picker', 'componentWillUpdate');
  }

  componentDidUpdate() {
    debug('nylas-calendar-picker', 'componentDidUpdate');
  }

  componentWillRender() {
    debug('nylas-calendar-picker', 'componentWillRender');
  }

  componentDidRender() {
    debug('nylas-calendar-picker', 'componentDidRender');
  }

  @Listen('selectedOptionsChanged')
  selectedOptionsChangedHandler(event: CustomEvent<{ value: string[]; name: string }>) {
    debug('nylas-calendar-picker', 'nylasFormDropdownChangedHandler', event.detail);
    // Pass as handler so that if event.defaultPrevented by parent app, this will be skipped.
    const valueChangedHandler = (event: CustomEvent<{ value: string[]; name: string }>) => {
      const { value } = event.detail;
      this.selectedCalendars = [...value];
      if (this.selectedCalendars.length === 0) {
        this.error = i18next.t('nylasCalendarPicker.selectAtLeastOneCalendar');
        this.internals.setValidity({ customError: true }, this.error);
      } else {
        this.error = '';
        this.internals.setValidity({});
      }
      this.internals.setFormValue(value.toString(), this.name);
    };
    this.valueChanged.emit({ ...event.detail, name: this.name, valueChangedHandler });
  }

  applyThemeConfig(themeConfig?: ThemeConfig) {
    if (themeConfig) {
      for (const [key, value] of Object.entries(themeConfig)) {
        this.host.style.setProperty(`${key}`, value);
      }
    }
  }

  @RegisterComponent<NylasCalendarPicker, NylasSchedulerConfigConnector, Exclude<NylasSchedulerEditor['stores'], undefined>>({
    name: 'nylas-calendar-picker',
    stateToProps: new Map([
      ['schedulerConfig.currentUser', 'currentUser'],
      ['schedulerConfig.selectedLanguage', 'selectedLanguage'],
      ['schedulerConfig.themeConfig', 'themeConfig'],
    ]),
    eventToProps: {
      valueChanged: async (
        event: CustomEvent<{ value: string[]; name: string; valueChangedHandler?: (event: CustomEvent<{ value: string[]; name: string }>) => void }>,
        _nylasSchedulerConfigConnector: NylasSchedulerConfigConnector,
      ) => {
        const { valueChangedHandler } = event.detail;
        // If a handler is passed, call it.
        if (valueChangedHandler) {
          valueChangedHandler(event);
        }
      },
    },
    fireRegisterEvent: true,
  })
  render() {
    const calendarOptions =
      this.calendars?.map(calendar => {
        return {
          value: calendar.id,
          label: calendar.name,
          selected:
            this.defaultSelectedCalendars?.includes(calendar.id) ||
            (this.defaultSelectedCalendars?.includes('primary') || this.defaultSelectedCalendars?.length === 0
              ? calendar.id === this.currentUser?.email || calendar.id === this.name.split('participant-')[1]
              : false),
        };
      }) ?? [];

    return (
      <Host>
        <div class="nylas-calendar-picker" part="ncp">
          {calendarOptions.length > 0 ? (
            <div class="nylas-calendar-picker__dropdown">
              <multi-select-dropdown
                name={this.name}
                options={calendarOptions}
                selectAtLeastOneOptionLabel={i18next.t('selectOneOrMoreOptions')}
                multipleOptionsSelectedLabel={i18next.t('multipleOptionsSelected')}
                error={this.error}
                exportparts="msd_dropdown: ncp__dropdown, msd_dropdown-button: ncp__dropdown-button, msd_dropdown-content: ncp__dropdown-content"
                themeConfig={this.themeConfig}
              ></multi-select-dropdown>
            </div>
          ) : (
            <div class="nylas-calendar-picker__loading">
              <span class="label">
                {i18next.t('nylasCalendarPicker.calendarPickerLabel')} <info-icon />
              </span>
              <div class="loading-button">
                <span>{i18next.t('loading')}...</span>
              </div>
            </div>
          )}
        </div>
      </Host>
    );
  }
}
